import { defineStore } from 'pinia';
import Network from "~/utils/network";
import { B2B_KYC_SUB_STEP, KYC_HOST_FLOW } from '~~/utils/const';
import { useB2bFlowStore } from './b2bFlow';
import { useBnplApplicationStore } from './bnplapplication';

const urls = {
  // `introKyc` tells the server this identification is the gate at the start of an igénylés, not a
  // flow question - only those fall back to a default expiry when the offer configures none.
  startKyc: (publicId, introKyc) => `/customer/identiff/kyc/identification/${publicId}?introKyc=${!!introKyc}`, // GET
  startCustomerLimitKyc: (customerLimitId) => `/customer/identiff/kyc/identification/customer-limit/${customerLimitId}`, // GET
  restartKyc: (publicId, introKyc) => `/customer/identiff/kyc/identification/restart/${publicId}?introKyc=${!!introKyc}`,
  startManagerKyc: (publicId) => `/customer/identiff/kyc/identification/manager/${publicId}`, // GET
  getKycByCustomerId: (customerId) => `/customer/identiff/kyc/${customerId}`, // GET
  getMyKyc: (publicId) => `/customer/identiff/kyc/my-customer/${publicId}`, // GET
  cancelMyKyc: (publicId) => `/customer/identiff/kyc/my-identification/cancel/${publicId}`, // POST

  // B2B only:
  b2bCustomerUpdate: `/customer/b2b/update`, // POST
  
  // B2C only:
  b2cCustomerUpdate: `/customer/b2c/update`, // POST
  getActiveKycByCustomer: `/customer/identiff/kyc/get-active-kyc-by-customer`,
  skipKyc: (publicId) => `/customer/identiff/kyc/skip/${publicId}`,
};

export const useKycStore = defineStore({
  id: 'kycStore',
  state: () => ({
    currentPosition: null,
    kyc: {},
    customerData: {},
    activeKyc: false,
    hostFlow: KYC_HOST_FLOW.BNPL_APPLICATION,
    // What this identification is keyed on - an application publicId, or a synthetic customer limit
    // reference. Set by IdentiffKyc so the sub-step components do not each have to derive it.
    reference: null,
    // Whether this identification is the gate at the start of an igénylés. The server needs to know:
    // only an intro KYC gets a default expiry when the offer configures none.
    introKyc: false,
  }),
  actions: {
    /**
     * Clears everything carried over from a previous identification. This store outlives the
     * components that use it, and a client-side route change does not reset it - so a KYC that was
     * left REJECTED can otherwise be mistaken for the current one and close a brand new igénylés.
     */
    resetKyc() {
      this.currentPosition = null;
      this.kyc = {};
      this.customerData = {};
      this.activeKyc = false;
      this.reference = null;
      this.hostFlow = KYC_HOST_FLOW.BNPL_APPLICATION;
      this.introKyc = false;
    },
    /**
     * Starting an identification is the one step that differs per host flow: an application is
     * identified by its publicId, the customer limit by its own id. Everything afterwards - polling,
     * cancelling, skipping - works off the reference, so only this needs to branch.
     */
    async startIdentification(reference) {
      if (this.hostFlow === KYC_HOST_FLOW.CUSTOMER_LIMIT) {
        // For the customer limit the reference is the limit id itself.
        const response = await Network.http(urls.startCustomerLimitKyc(reference), { method: 'GET' });
        this.kyc = response;
        return response;
      }
      return this.startKyc(reference);
    },
    async startKyc(publicId) {
      try {
        const response = await Network.http(urls.startKyc(publicId, this.introKyc), { method: 'GET' });
        this.kyc = response;
        return response;
      } catch (err) {
        // console.error(`Error starting KYC with publicId: ${publicId}`);
        throw err; // Propagate the error
      }
    },
    async restartKyc(publicId) {
      try {
        this.activeKyc = false;
        const response = await Network.http(urls.restartKyc(publicId, this.introKyc), { method: 'GET' });
        this.kyc = response;
        return response;
      } catch (err) {
        // console.error(`Error starting KYC with publicId: ${publicId}`);
        throw err; // Propagate the error
      }
    },
    async startManagerKyc(publicId) {
      try {
        const response = await Network.http(urls.startManagerKyc(publicId), { method: 'GET' });
        this.kyc = response;
        return response;
      } catch (err) {
        // console.error(`Error starting KYC with publicId: ${publicId}`);
        throw err; // Propagate the error
      }
    },
    async getMyKyc(publicId) {
      try {
        const response = await Network.http(urls.getMyKyc(publicId), { method: 'GET' });
        this.kyc = response;
        return response;
      } catch (err) {
        // console.error(`Error fetching my KYC for application with publicId: ${publicId}`);
        throw err; // Propagate the error
      }
    },
    async cancelMyKyc(publicId) {
      try {
        await Network.http(urls.cancelMyKyc(publicId), { method: 'POST' });
        this.kyc = {}; // Reset KYC state
      } catch (err) {
        // console.error(`Error cancelling my KYC for application with publicId: ${publicId}`);
        throw err; // Propagate the error
      }
    },
    async updateCustomer(isB2b, customerData) {
      try {
        console.log("Updating customer data", { isB2b, customerData });
        if (!isB2b) {
          const response = await Network.http(urls.b2cCustomerUpdate, { method: 'POST', body: customerData });
          return response;
        } else {
          const response = await Network.http(urls.b2bCustomerUpdate, { method: 'POST', body: customerData });
          return response;
        }
      } catch (err) {
        // console.error(`Error cancelling my KYC for application with publicId: ${publicId}`);
        throw err; // Propagate the error
      }
    },
    async getActiveKycByCustomer() {
      try {
        const response = await Network.http(urls.getActiveKycByCustomer, { method: 'GET' });
        this.activeKyc = true;
        this.kyc = response;
      } catch (err) {
        throw err; // Propagate the error
      }
    },
    async skipKyc(publicId) {
      try {
        await Network.http(urls.skipKyc(publicId), { method: 'PATCH' });
      } catch (err) {
        throw err; // Propagate the error
      }
    },
    async nextStep(isB2b = false, isManager = false) {
      if (!isB2b) {
        const bnplApplicationStore = useBnplApplicationStore();
        switch (this.currentPosition) {
          case B2B_KYC_SUB_STEP.INTRO:
            this.currentPosition = B2B_KYC_SUB_STEP.CODE;
            break;
          case B2B_KYC_SUB_STEP.RESULT:
            this.currentPosition = B2B_KYC_SUB_STEP.DATA_CHECK;
            break;
          case B2B_KYC_SUB_STEP.DATA_CHECK:
            try {
              await this.updateCustomer(false, this.customerData);
              // Awaited: callers re-read flow state straight after this resolves, and an
              // un-awaited step would let them act on the position it has not set yet.
              await bnplApplicationStore.nextStep();
            } catch (error) {
              throw new Error(error.statusCode);
            }
            break;
        }
      } else {
        const b2bFlowStore = useB2bFlowStore();
        if (!isManager) {
          switch (this.currentPosition) {
            case B2B_KYC_SUB_STEP.INTRO:
              this.currentPosition = B2B_KYC_SUB_STEP.CODE;
              break;
            case B2B_KYC_SUB_STEP.RESULT:
              this.currentPosition = B2B_KYC_SUB_STEP.DATA_CHECK;
              break;
            case B2B_KYC_SUB_STEP.DATA_CHECK:
              try {
                await this.updateCustomer(true, this.customerData);
              } catch (error) {
                throw new Error(error.statusCode);
              }
          }
        } else {
          switch (this.currentPosition) {
            case B2B_KYC_SUB_STEP.INTRO:
              this.currentPosition = B2B_KYC_SUB_STEP.CODE;
              break;
            case B2B_KYC_SUB_STEP.RESULT:
              this.currentPosition = B2B_KYC_SUB_STEP.DATA_CHECK;
              break;
            case B2B_KYC_SUB_STEP.DATA_CHECK:
              try {
                await b2bFlowStore.updateManager(this.customerData);
                this.currentPosition = B2B_KYC_SUB_STEP.OUTRO;
              } catch (error) {
                throw new Error(error.statusCode);
              }
              break;
            case B2B_KYC_SUB_STEP.OUTRO:
              this.currentPosition = B2B_KYC_SUB_STEP.CODE;
              break;
          }
        }
      }
    },
  },
  getters: {
    url: (state) => {
      return state.kyc.tokenAuthUrl || "";
    },
    status: (state) => {
      return state.kyc.status;
    },
    progressStep: (state) => {
      return state.kyc.progressStep
    },
    isCustomerDataFilled(state) {
      for (const [key, value] of Object.entries(state.customerData)) {
        console.log(key, value)
        if (key !== 'mothersName' && !value) {
          return false
        }
      }
      return true
    },
  }
})
